import { ButtonSizesEnum, ButtonColorsEnum } from '@signplatform-ui/button';
import { HeaderSizesEnum } from '@signplatform-ui/header';
import {
  PublicLayoutWithLogo,
  StyledHeader,
  StyledTextInfo,
} from 'modules/Layouts/PublicLayout';
import { useConfiguration, useStorage } from 'modules/Shared/hooks';
import Footer, { FooterColorEnum } from 'modules/UI/components/Footer';
import { parseBoolean } from 'services/commonUtils';
import { memo, useCallback } from 'react';
import type { ReactNode, MouseEventHandler } from 'react';
import goToAuth from './goToAuth';
import { ButtonLogin, LinkStyled } from './Login.styled';

type LoginContentProps = {
  isAttention?: boolean;
  onAuthClick: MouseEventHandler<HTMLButtonElement>;
};

const LoginContent = memo(
  ({ isAttention, onAuthClick }: LoginContentProps): JSX.Element => {
    return (
      <div>
        <StyledHeader type={HeaderSizesEnum.H1}>
          Платформа
          <br />
          подписания
        </StyledHeader>

        {isAttention && (
          <>
            <StyledTextInfo isAttention>Уважаемый пользователь!</StyledTextInfo>

            <StyledTextInfo isAttention>
              В настоящее время наблюдается повышенная активность пользователей,
              регистрация в «Автографе» может занимать чуть больше времени. В
              случае неуспешной авторизации рекомендуем повторить попытку через
              два часа.
            </StyledTextInfo>
          </>
        )}

        <ButtonLogin
          color={ButtonColorsEnum.White}
          onClick={onAuthClick}
          size={ButtonSizesEnum.Large}
        >
          Войти через Госуслуги
        </ButtonLogin>

        <StyledTextInfo>
          Чтобы войти, пожалуйста, убедитесь, что ваша учетная запись
          подтверждена на портале Госуслуг
        </StyledTextInfo>

        <LinkStyled href='https://esia.gosuslugi.ru/registration/'>
          Нет подтвержденной учетной записи Госуслуг
        </LinkStyled>
      </div>
    );
  }
);

const Login = ({ children }: { children?: ReactNode }): JSX.Element => {
  const [{ attention, authClientId, authPrefix }] = useConfiguration();
  const isAttention = parseBoolean(attention);

  const { storageWriteData } = useStorage(false);

  const handleWriteStorage = useCallback(() => {
    storageWriteData({
      loggedInRedirect: window.location.pathname,
    });
  }, [storageWriteData]);

  const handleGoToAuth: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      handleWriteStorage();
      goToAuth(e, authClientId, authPrefix);
    },
    [authClientId, authPrefix, handleWriteStorage]
  );

  const content = (
    <LoginContent isAttention={isAttention} onAuthClick={handleGoToAuth} />
  );
  const footer = <Footer color={FooterColorEnum.secondary} />;

  return (
    <PublicLayoutWithLogo slotContent={content} footer={footer}>
      {children}
    </PublicLayoutWithLogo>
  );
};

export default Login;
